import { jsx } from 'react/jsx-runtime';
import _get from 'lodash.get';
import { createContext, useState, useEffect, useContext } from 'react';
import { validations } from '../utils/formUtils.js';
import { usePaymentContext } from './PaymentContextProvider.js';

const FormContext = createContext({
    setFormData: () => ({ value: '' }),
    getFormData: () => ({ value: '' }),
    initForm: () => { },
    validate: () => false,
    clearForm: () => { },
});
const useFormContext = () => useContext(FormContext);
const FormContextProvider = ({ adyenCreditCardFormRef, braintreeCreditCardFormRef, children, }) => {
    const { selectedPayment } = usePaymentContext();
    const sepaData = selectedPayment?.details?.bankAccountDetails;
    const initialFormData = {
        sepa: {
            holderName: {
                value: sepaData?.owner || '',
                validations: ['required', 'nonNumeric'],
            },
            iban: {
                value: sepaData?.number || '',
                validations: ['required', 'iban'],
            },
        },
        creditCard: {
            number: { value: '', validations: ['required', 'cardNumber'] },
            holderName: { value: '', validations: ['required', 'min:3', 'max:30'] },
            brand: { value: '', validations: ['required'] },
            expirationMonth: { value: '', validations: ['required', 'expirationDate'] },
            expirationYear: { value: '', validations: ['required', 'expirationDate'] },
        },
        bancontact: {
            number: { value: '', validations: ['required', 'cardNumber'] },
            holderName: { value: '', validations: ['required', 'min:3', 'max:30'] },
            brand: { value: '', validations: ['required'] },
            expirationMonth: { value: '', validations: ['required', 'expirationDateBC'] },
            expirationYear: { value: '', validations: ['required', 'expirationDateBC'] },
        },
    };
    const [formData, setFormDataBase] = useState(initialFormData);
    const [activeForm, setActiveForm] = useState();
    const validate = (form, force) => {
        if (!form || !selectedPayment)
            return false;
        const inputs = Object.keys(formData[form]);
        if (force) {
            inputs.forEach((input) => {
                const formInputs = formData[form];
                setFormData(form, input, formInputs[input].value);
            });
        }
        const isFormValid = inputs.reduce((acc, input) => {
            const formInputs = formData[form];
            return (acc &&
                !!formInputs[input].validations?.reduce((acc, initialValidation) => {
                    const validation = initialValidation.split(':')?.[0];
                    const validationParams = initialValidation.split(':')?.[1];
                    if (validation.includes('expirationDate')) {
                        return (acc &&
                            validations[validation](`${formInputs.expirationMonth.value}-${formInputs.expirationYear.value}`));
                    }
                    return acc && validations[validation](formInputs[input].value, validationParams);
                }, true));
        }, true);
        return isFormValid;
    };
    const setFormData = (form, key, value) => {
        const formInputs = formData[form];
        const formInput = formData[form][key];
        const isInputValid = formInput?.validations?.reduce((acc, initialValidation) => {
            const validation = initialValidation.split(':')?.[0];
            const validationParams = initialValidation.split(':')?.[1];
            if (validation.includes('expirationDate')) {
                const date = key === 'expirationMonth'
                    ? `${value}-${formInputs.expirationYear.value}`
                    : `${formInputs.expirationMonth.value}-${value}`;
                return acc && validations[validation](date, validationParams);
            }
            return acc && validations[validation](value, validationParams);
        }, true);
        setFormDataBase((formData) => ({
            ...formData,
            [form]: {
                ...formData[form],
                ...(['expirationMonth', 'expirationYear'].includes(key)
                    ? {
                        expirationMonth: {
                            ...formInputs.expirationMonth,
                            value: key === 'expirationMonth' ? value : formInputs.expirationMonth.value,
                            hasError: !isInputValid,
                        },
                        expirationYear: {
                            ...formInputs.expirationYear,
                            value: key === 'expirationYear' ? value : formInputs.expirationYear.value,
                            hasError: !isInputValid,
                        },
                    }
                    : {
                        [key]: {
                            ...formInput,
                            value: value,
                            hasError: !isInputValid,
                        },
                    }),
            },
        }));
    };
    const getFormData = (form, key) => {
        return _get(formData, `${form}.${String(key)}`, { value: '' }) || { value: '' };
    };
    const initForm = (form) => {
        setActiveForm(form);
        validate(form);
    };
    const clearForm = () => {
        setActiveForm(undefined);
    };
    useEffect(() => {
        validate(activeForm);
    }, [formData, activeForm, validate]);
    return (jsx(FormContext.Provider, { value: {
            adyenCreditCardFormRef,
            braintreeCreditCardFormRef,
            formData,
            setFormData,
            getFormData,
            initForm,
            validate,
            clearForm,
        }, children: children }));
};

export { FormContext, FormContextProvider, FormContextProvider as default, useFormContext };
//# sourceMappingURL=FormContextProvider.js.map
